import React, { useEffect, useRef } from "react";

const ChatMessages = ({ messages, setMessages, apiUrl, chatId, user }) => {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;

    // If user is near the bottom, keep auto-scroll on; otherwise, turn it off
    shouldAutoScrollRef.current = distanceFromBottom < 60;
  };

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 flex flex-col overflow-y-auto px-3 py-3 space-y-3"
    >
      {messages.map((m, i) => {
        const formattedMessage =
          m.role === "assistant"
            ? m.text
              .replace(/\*\*/g, "")
              .replace(/\*/g, "•")
              .replace(/\n/g, "<br/>")
            : m.text;

        const handleFeedbackClick = async (index, value) => {
          const target = messages[index];

          if (target.feedback === value) {
            setMessages((prev) =>
              prev.map((mm, idx) =>
                idx === index ? { ...mm, feedback: null, _hover: false } : mm
              )
            );

            let userMsg = "";
            for (let k = index - 1; k >= 0; k--) {
              if (messages[k].role === "user") {
                userMsg = messages[k].text;
                break;
              }
            }

            await fetch(`${apiUrl}/v1/feedback`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                chat_id: chatId,
                user_msg: userMsg,
                assistant_msg: target.text,
                reaction: null,
                user_id: user?.email || null,
              }),
            });

            return;
          }

          setMessages((prev) =>
            prev.map((mm, idx) =>
              idx === index ? { ...mm, feedback: value, _hover: false } : mm
            )
          );

          let userMsg = "";
          for (let k = index - 1; k >= 0; k--) {
            if (messages[k].role === "user") {
              userMsg = messages[k].text;
              break;
            }
          }

          await fetch(`${apiUrl}/v1/feedback`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              chat_id: chatId,
              user_msg: userMsg,
              assistant_msg: target.text,
              reaction: value,
              user_id: user?.email || null,
            }),
          });
        };

        return (
          <div
            key={i}
            className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"
              }`}
            onMouseEnter={() => {
              if (m.role === "assistant") {
                setMessages((prev) =>
                  prev.map((mm, idx) =>
                    idx === i ? { ...mm, _hover: true } : mm
                  )
                );
              }
            }}
            onMouseLeave={() => {
              if (m.role === "assistant") {
                setMessages((prev) =>
                  prev.map((mm, idx) =>
                    idx === i ? { ...mm, _hover: false } : mm
                  )
                );
              }
            }}
          >
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white text-[11px] font-bold flex items-center justify-center shadow">
                HR
              </div>
            )}
            {m.role === "assistant" ? (
              <div className="relative inline-block max-w-[80%] rounded-3xl rounded-tl-sm bg-slate-100 text-slate-800 px-4 py-3 leading-relaxed text-sm shadow-sm text-left">
                <div
                  dangerouslySetInnerHTML={{ __html: formattedMessage }}
                />

                {m._hover && !m.feedback && (
                  <div className="absolute -right-9 top-1/2 -translate-y-1/2 flex gap-1 bg-white rounded-full px-1.5 py-1 shadow-md">
                    <span
                      className="cursor-pointer text-lg hover:scale-125 transition-transform"
                      onClick={() => handleFeedbackClick(i, "up")}
                    >
                      👍
                    </span>
                    <span
                      className="cursor-pointer text-lg hover:scale-125 transition-transform"
                      onClick={() => handleFeedbackClick(i, "down")}
                    >
                      👎
                    </span>
                  </div>
                )}

                {m.feedback && (
                  <div
                    className="absolute -bottom-2 -right-2 bg-white text-xs rounded-full px-1.5 py-0.5 shadow-md cursor-pointer"
                    onClick={() => handleFeedbackClick(i, m.feedback)}
                  >
                    {m.feedback === "up" ? "👍" : "👎"}
                  </div>
                )}
              </div>
            ) : (
              <div
                className="max-w-[70%] rounded-3xl rounded-br-sm bg-gradient-to-r from-primaryPurple to-secondaryPurple text-white px-4 py-2 text-sm text-right shadow-sm"
                dangerouslySetInnerHTML={{ __html: formattedMessage }}
              />
            )}

            {m.role === "user" && (
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white text-[11px] font-bold flex items-center justify-center shadow">
                ME
              </div>
            )}
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatMessages;

